/**
 * # dxfBridge.mleaderTextStyleStamp — MLEADER text-style handle stamping (export)
 *
 * Extracted verbatim from `dxfBridge.export.ts` (T9b) to keep the orchestrator
 * under the 2000-line guard; the single caller is `exportDrawingDocumentToDxfDocument`.
 * Behaviour, comments and the Codex finding references are unchanged except for
 * the T9b Standard re-resolution documented inline.
 */
import type { DxfEntity, DxfTextStyleEntry } from "@dxf-ai-editor/dxf-io";

import { createMissingTextStyleEntry } from "./dxfBridge.textStyleRecord";

/**
 * Stamp MLEADER entity text-style handles (group 340).
 *
 * The MLEADER content text style round-trips by NAME on the typed model
 * (`MultileaderEntity.textStyleName`); AutoCAD persists it in the DXF as a
 * HANDLE (group 340 inside CONTEXT_DATA / MTextData) pointing at a STYLE
 * table record. On export we resolve that name → the STYLE entry's handle
 * so the writer re-emits a REAL group 340 (never fabricated). When a
 * referenced STYLE entry lacks a handle (pure-model export assigns none),
 * we mint a deterministic synthetic handle and stamp it onto BOTH the
 * STYLE entry and every MLEADER that references it — so the emitted 340
 * resolves back to the same name on re-import. Standard-style MLEADERs
 * carry no `textStyleName` on the transport and are left untouched
 * (handle omitted, name defaults to "Standard" on re-import).
 *
 * `seedHandles` (Codex #243 F1) is the document-wide set of handles already
 * emitted by EVERY other section (entities, blocks, OBJECTS, dynamic
 * blocks, layouts, tables). The minted synthetic handle is collision-
 * checked against this full population — not just STYLE records — so it is
 * unique across the whole exported document.
 */
export function stampMLeaderTextStyleHandles(
  entities: DxfEntity[],
  textStyles: DxfTextStyleEntry[],
  seedHandles?: ReadonlySet<string>,
): void {
  // Index STYLE entries by name for quick lookup / handle assignment.
  //
  // Codex #243 F4: STYLE names are case-INSENSITIVE in DXF (the rest of
  // the style-table logic lowercases — e.g. Standard seeding). Indexing
  // by original casing would treat MLEADER `textStyleName: "mystyle"` as
  // MISSING when the STYLE table already holds `MyStyle`, minting a
  // DUPLICATE STYLE entry + a wrong handle. Key the index by lowercase so
  // an existing record is reused regardless of case.
  const styleByName = new Map<string, DxfTextStyleEntry>();
  for (const style of textStyles) styleByName.set(style.name.toLowerCase(), style);
  // T9b — reverse index (lowercased group-5 → STYLE) so a stale MLEADER 340 can
  // be told apart from a dangling one (see the Standard branch below).
  const styleByHandle = new Map<string, DxfTextStyleEntry>();
  for (const style of textStyles) {
    if (style.handle) styleByHandle.set(style.handle.toLowerCase(), style);
  }

  // Codex #243 F5: DXF group-5 / group-340 handles MUST be hexadecimal
  // strings. The old `MTS<n>` scheme minted NON-hex handles (e.g. "MTS1")
  // which are invalid in a DXF handle slot. Mint from a high HEX range
  // (0x100000+), the same convention the dynamic-block writer uses
  // (`makeContext` in dxfBridge.dynamic.writer.ts).
  //
  // Codex #243 F1: collision-avoid against EVERY handle the document emits,
  // not just STYLE records. The dynamic-block writer mints from the SAME
  // 0x100000 range and entity / block / OBJECTS handles share the same
  // magnitude, so a STYLE-only seed could mint a duplicate group-5 →
  // ambiguous group-340. Seed from the document-wide handle set (passed in)
  // plus the local STYLE handles (belt-and-suspenders; STYLE handles are
  // also part of the document-wide set when sections are assembled).
  const usedHandles = new Set<string>(seedHandles ?? []);
  for (const style of textStyles) {
    if (style.handle) usedHandles.add(style.handle.toLowerCase());
  }
  let handleSeq = 0x100000;
  const nextSyntheticHandle = (): string => {
    let candidate: string;
    do {
      handleSeq += 1;
      candidate = handleSeq.toString(16).toUpperCase();
    } while (usedHandles.has(candidate.toLowerCase()));
    usedHandles.add(candidate.toLowerCase());
    return candidate;
  };

  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "MLEADER") {
      continue;
    }
    const name = entity.geometry.textStyleName;
    // Standard carries no handle on the transport (defaults to "Standard"
    // on re-import); compare case-insensitively so "standard"/"STANDARD"
    // are also left untouched, matching the rest of the table logic.
    //
    // Codex #270 NON-MANIFESTING (re-verified — see cleanup Q5 report): the audit
    // asked to CLEAR a stale `mTextData.styleHandle` left over from switching to
    // Standard. But (1) an UNCONDITIONAL clear breaks the A-D byte-faithful
    // contract — mtext.dxf's Standard MLEADER carries a dangling 340 (handle
    // "29") that MUST round-trip verbatim, and a switched-to-Standard MLEADER's
    // stale handle is INDISTINGUISHABLE from it at this layer (no edit-tracking);
    // and (2) the re-resolve bug does NOT occur: the export emits STYLE records
    // WITHOUT group-5 handles and mints MLEADER style handles in a disjoint
    // 0x100000+ range, so a stale source handle never matches a live STYLE entry
    // → it never re-resolves and textStyleName correctly returns to "Standard".
    // A correct clear needs edit-tracking at the command/property layer (out of
    // this category-B dxf-round-trip scope). So: preserve verbatim (faithful).
    //
    // T9b — premise (2) no longer holds: STYLE records now carry their group-5
    // (a real AutoCAD file always did; the conformant export mints one), so a
    // stale `mTextData.styleHandle` WOULD re-resolve to the old custom style on
    // reopen. The two cases are now DISTINGUISHABLE without edit-tracking:
    //   - the stale handle resolves to a LIVE non-Standard STYLE record → the
    //     user switched to Standard; re-home the 340 onto the Standard record
    //     (AutoCAD rewrites the context's style handle on that property edit);
    //   - it dangles (mtext.dxf's "29") or already IS Standard → untouched.
    // This can only fire after a MODEL edit: the import resolves
    // `textStyleName` FROM the 340 (`handleResolution.ts`), so on a plain
    // import → export round-trip the name and the handle always agree and the
    // byte-faithful path is untouched.
    const isStandard = !name || name.toLowerCase() === "standard";
    if (isStandard) {
      const stale = entity.geometry.contextData?.mTextData?.styleHandle;
      const resolved = stale ? styleByHandle.get(stale.toLowerCase()) : undefined;
      if (!resolved || resolved.name.toLowerCase() === "standard") continue;
    }
    const styleName = isStandard ? "Standard" : name;
    let style = styleByName.get(styleName.toLowerCase());
    if (!style) {
      // The named style is not in the STYLE table (e.g. the doc carried the
      // name on the entity but no matching style record). Create the FULL
      // canonical STYLE record (typed default payload + the table-head owner
      // its siblings carry — T9b review round 1 P2; a bare `{ name }` record
      // emitted without `330` / `70 40 41 50 71 42 3 4`, which ezdxf silently
      // completed on load) so the 340 handle resolves on re-import. On the
      // conformant path the skeleton already materialised this record
      // (`materializeReferencedTextStyles`), so this is the non-conformant
      // fallback only.
      style = createMissingTextStyleEntry(styleName, textStyles);
      textStyles.push(style);
      styleByName.set(styleName.toLowerCase(), style);
    }
    if (!style.handle) style.handle = nextSyntheticHandle();
    entity.geometry.textStyleHandle = style.handle;
    // FIX 4 (sub-slice D follow-up) — keep the faithful-path 340 in sync. The
    // byte-faithful writer emits group 340 from contextData.mTextData.styleHandle
    // when an MTextData block is present (geom.textStyleHandle only feeds the
    // no-mTextData fallback). Without also updating the typed styleHandle, an
    // edited/new text style would export the STALE mTextData.styleHandle parsed
    // from the source. Stamp the resolved handle onto BOTH so either writer path
    // emits the correct 340. (The no-mTextData case is covered by the fallback
    // stamp the faithful path emits from textStyleHandle.)
    const mTextData = entity.geometry.contextData?.mTextData;
    const oldPrimaryStyleKey = mTextData?.styleHandle?.toLowerCase();
    if (mTextData) mTextData.styleHandle = style.handle;
    // #294 r3 — mirror the SAME decision onto every SECONDARY CONTEXT_DATA
    // MTextData copy. The writer emits each secondary block's group 340 from
    // its OWN copy (emitMTextData inside buildSecondaryContextBlock), so
    // restamping only the flat channel + primary (above) exported an edited/
    // re-resolved text style in the primary block while every per-scale
    // secondary still pointed at the STALE style handle. Same matched-copy
    // gate as restampMLeaderBlockHandles (the 341 precedent): only a copy
    // that pointed at the SAME handle the primary carried is provably the
    // same reference and follows the primary's decision; an independently-
    // divergent secondary 340 — or one on an entity whose primary carried no
    // 340 to compare against — is preserved verbatim, and an absent secondary
    // 340 is never invented (preserve-source-never-infer). A copy already at
    // the resolved handle is left untouched (byte-verbatim, incl. case).
    const newStyleKey = style.handle.toLowerCase();
    for (const sec of entity.geometry.secondaryContexts ?? []) {
      const secMText = sec.context.mTextData;
      if (!secMText?.styleHandle) continue;
      const secKey = secMText.styleHandle.toLowerCase();
      if (secKey === newStyleKey) continue;
      if (oldPrimaryStyleKey === undefined || secKey !== oldPrimaryStyleKey) {
        continue;
      }
      secMText.styleHandle = style.handle;
    }
  }
}
